import React from 'react';
import { Link } from 'react-router-dom';
import { daysUntil, formatEventRange } from '../../services/storefront';
import { STORE, STORE_MAPS_URL, mapsSearchUrl } from '../storefront/storeInfo';
import AccentText from '../storefront/AccentText';
import {
  IconArrowRight,
  IconArrowUpRight,
  IconCalendar,
  IconClock,
  IconMail,
  IconPhone,
  IconPin,
  Longhorn,
} from '../storefront/icons';

const MAX_EVENTS = 2;

const countdownLabel = (dates) => {
  if (!dates) return null;
  const days = daysUntil(dates.start);
  if (days > 1) return `${days} days to go`;
  if (days === 1) return 'Starts tomorrow';
  return 'Happening now';
};

// Admin descriptions often repeat the event title first; drop it.
const eventBlurb = (event) => {
  const description = (event.description || '').trim();
  if (description.toLowerCase().startsWith(event.title.toLowerCase())) {
    return description.slice(event.title.length).replace(/^[\s:–—-]+/, '');
  }
  return description;
};

const EventTicket = ({ event, label }) => {
  const { dates } = event;
  const blurb = eventBlurb(event);
  const month = dates ? dates.start.toLocaleString('en-US', { month: 'short' }) : null;
  const dayRange = dates
    ? (dates.start.getTime() === dates.end.getTime() || dates.start.getMonth() !== dates.end.getMonth()
      ? `${dates.start.getDate()}`
      : `${dates.start.getDate()}–${dates.end.getDate()}`)
    : null;

  return (
    <article className="tp-ticket" data-reveal style={{ '--reveal-delay': 120 }}>
      <div className="tp-ticket__main">
        <span className="tp-eyebrow tp-eyebrow--on-ink" data-copy-key="findUs.marketLabel">{label}</span>
        <h3 className="tp-display tp-ticket__title">{event.title}</h3>
        {blurb && <p className="tp-ticket__blurb">{blurb}</p>}
        <ul className="tp-ticket__facts">
          <li><IconCalendar size={17} /> {dates ? formatEventRange(dates) : event.event_date}</li>
          {event.location && <li><IconPin size={17} /> {event.location}</li>}
        </ul>
        {event.location && (
          <a className="tp-textlink tp-textlink--on-ink" href={mapsSearchUrl(event.location)} target="_blank" rel="noopener noreferrer">
            Get directions <IconArrowUpRight size={16} />
          </a>
        )}
      </div>
      <div className="tp-ticket__stub" aria-hidden={!dates}>
        {dates ? (
          <>
            <span className="tp-ticket__month">{month}</span>
            <span className="tp-ticket__days">{dayRange}</span>
            <span className="tp-ticket__year">{dates.start.getFullYear()}</span>
            <span className="tp-ticket__countdown">{countdownLabel(dates)}</span>
          </>
        ) : (
          <Longhorn className="tp-ticket__glyph" />
        )}
      </div>
    </article>
  );
};

const FindUs = ({ events, copy }) => {
  const shown = events.slice(0, MAX_EVENTS);

  return (
    <section className="tp-section tp-findus" aria-labelledby="tp-findus-title">
      <div className="tp-container">
        <header className="tp-section-head" data-reveal>
          <div>
            <span className="tp-eyebrow" data-copy-key="findUs.eyebrow">{copy['findUs.eyebrow']}</span>
            <h2 id="tp-findus-title" className="tp-display tp-section-head__title" data-copy-key="findUs.title">
              <AccentText text={copy['findUs.title']} />
            </h2>
          </div>
          <div className="tp-section-head__aside">
            <p data-copy-key="findUs.body">{copy['findUs.body']}</p>
          </div>
        </header>

        <div className={`tp-findus__grid${shown.length ? '' : ' tp-findus__grid--solo'}`}>
          <article className="tp-place" data-reveal>
            <div className="tp-place__head">
              <span className="tp-eyebrow" data-copy-key="findUs.showroomLabel">{copy['findUs.showroomLabel']}</span>
              <h3 className="tp-display tp-place__city" data-copy-key="findUs.showroomTitle">{copy['findUs.showroomTitle']}</h3>
            </div>
            <ul className="tp-place__rows">
              <li>
                <IconPin size={18} />
                <span>{STORE.street}<br />{STORE.cityLine}</span>
              </li>
              <li>
                <IconClock size={18} />
                <span>{STORE.hours}</span>
              </li>
              <li>
                <IconPhone size={18} />
                <span>
                  <a href={STORE.phone.href}>{STORE.phone.label}</a>
                  <span className="tp-place__muted"> · Toll-free </span>
                  <a href={STORE.tollFree.href}>{STORE.tollFree.label}</a>
                </span>
              </li>
              <li>
                <IconMail size={18} />
                <a href={STORE.email.href}>{STORE.email.label}</a>
              </li>
            </ul>
            <div className="tp-place__actions">
              <a className="tp-btn tp-btn--small tp-btn--icon" href={STORE_MAPS_URL} target="_blank" rel="noopener noreferrer">
                Get directions
                <span className="tp-btn__icon"><IconArrowUpRight size={16} /></span>
              </a>
              <Link to="/contact" className="tp-textlink">
                Contact us <IconArrowRight size={16} />
              </Link>
            </div>
            <Longhorn className="tp-place__glyph" />
          </article>

          {shown.map((event) => (
            <EventTicket key={event.id} event={event} label={copy['findUs.marketLabel']} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FindUs;
